import React from 'react';
import type { ReactNode, FC, PropsWithChildren } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, xcss, Stack, Inline, Anchor, type AnchorProps } from '@atlaskit/primitives';
import PremiumIcon from '@atlaskit/icon/core/premium';
import { token } from '@atlaskit/tokens';
import CloseIcon from '@atlaskit/icon/core/close';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';

const i18n = defineMessages({
	header: {
		id: 'premium-trial.premium-tip-prompt.header',
		defaultMessage: 'Premium tip',
		description: 'Header of the premium tip prompt',
	},
	close: {
		id: 'premium-trial.premium-tip-prompt.close',
		defaultMessage: 'Close Premium tip',
		description: 'Label for close button',
	},
});

const promptContainerStyles = xcss({
	width: '387px',
	borderRadius: 'borderRadius.medium',
	boxShadow: 'elevation.shadow.overlay',
});

const headerStyles = xcss({
	color: 'color.text.accent.purple',
});

const titleStyles = xcss({
	font: token('font.heading.xsmall'),
});

const descriptionStyles = xcss({
	font: token('font.body'),
});

const actionStyles = xcss({
	textDecoration: 'none',
});

const primaryActionStyles = xcss({
	color: 'color.text.accent.purple',

	':hover': {
		color: 'color.text.accent.purple',
	},

	':visited': {
		color: 'color.text.accent.purple',
	},
	':visited:active': {
		color: 'color.text.accent.purple',
	},
});

const secondaryActionStyles = xcss({
	color: 'color.text.accent.gray',

	':hover': {
		color: 'color.text.accent.gray',
	},

	':visited': {
		color: 'color.text.accent.gray',
	},
	':visited:active': {
		color: 'color.text.accent.gray',
	},
});

type ActionProps = PropsWithChildren<{
	link?: Pick<AnchorProps, 'href' | 'target'>;
	onClick?: () => void;
	testId: string;
	appearance?: 'primary' | 'subtle';
}>;

type ActionType = Pick<ActionProps, 'link' | 'onClick'> & {
	content: string | ReactNode;
};
export type PremiumTipPromptProps = {
	title: string | ReactNode;
	description: string | ReactNode;
	onClose?: () => void;
	primaryAction?: ActionType;
	secondaryAction?: ActionType;
};

const Action: FC<ActionProps> = ({ testId, children, link, onClick, appearance = 'primary' }) => {
	return (
		<Anchor
			testId={testId}
			role={link ? 'link' : 'button'}
			xcss={[actionStyles, appearance === 'primary' ? primaryActionStyles : secondaryActionStyles]}
			href={link?.href || '#'}
			target={link?.target}
			onClick={onClick}
		>
			{children}
		</Anchor>
	);
};

export const PremiumTipPrompt: FC<PremiumTipPromptProps> = ({
	title,
	description,
	onClose,
	primaryAction,
	secondaryAction,
}) => {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={promptContainerStyles}
			paddingBlockEnd="space.200"
			backgroundColor="elevation.surface.overlay"
		>
			<Stack space="space.150">
				<Box
					backgroundColor="color.background.accent.purple.subtlest"
					paddingBlock="space.075"
					paddingInlineStart="space.300"
					paddingInlineEnd="space.150"
				>
					<Inline alignBlock="center" spread="space-between">
						<Inline space="space.100" alignBlock="center">
							<PremiumIcon
								color={token('color.icon.accent.purple')}
								label={formatMessage(i18n.header)}
							/>
							<Heading size="xsmall" as="div">
								<Box xcss={headerStyles}>{formatMessage(i18n.header)}</Box>
							</Heading>
						</Inline>
						<IconButton
							appearance="subtle"
							icon={CloseIcon}
							onClick={onClose}
							label={formatMessage(i18n.close)}
						/>
					</Inline>
				</Box>

				<Box paddingInline="space.300">
					<Stack space="space.100">
						<Box xcss={titleStyles}>{title}</Box>
						<Box xcss={descriptionStyles}>{description}</Box>
						<Inline space="space.100" separator="·">
							{primaryAction && (
								<Action
									testId="premium-tip-primary-action"
									link={primaryAction.link}
									onClick={primaryAction.onClick}
								>
									{primaryAction.content}
								</Action>
							)}
							{secondaryAction && (
								<Action
									testId="premium-tip-secondary-action"
									link={secondaryAction.link}
									onClick={secondaryAction.onClick}
									appearance="subtle"
								>
									{secondaryAction.content}
								</Action>
							)}
						</Inline>
					</Stack>
				</Box>
			</Stack>
		</Box>
	);
};
